// src/pages/public/VerifyEmail.jsx
// FASE 1.B — pixel-perfect del mockup v4 screens_auth.jsx
//
// UX audit ISSUE 9:
//  - Si el usuario ya pagó (hasAccess=true), el link secundario es
//    "Ir al dashboard", no "Continuar sin verificar" hacia /paywall.
//  - Botón nuevo "Ya verifiqué — continuar" que recarga el currentUser
//    de Firebase y redirige al destino correcto si emailVerified=true.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { resendVerification, authErrorMessage } from '../../auth/authHelpers';
import { AuthLogo, ErrorBanner } from './_authShared';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, hasAccess, signOut } = useAuth();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleResend = async () => {
    setError('');
    try {
      await resendVerification();
      setSent(true);
    } catch (err) {
      setError(authErrorMessage(err));
    }
  };

  // ISSUE 9: chequea si el usuario ya verificó (clickeó el link en su email).
  // Recarga el currentUser de Firebase y redirige según hasAccess.
  // Sync Firestore en éxito (mismo patrón que AuthAction.jsx) para
  // disparar onUserVerifiedSendWelcome sin depender del backfill.
  const handleAlreadyVerified = async () => {
    setError('');
    setRefreshing(true);
    try {
      const auth = getAuth();
      if (auth.currentUser) await auth.currentUser.reload();
      if (auth.currentUser?.emailVerified) {
        try {
          await updateDoc(doc(firestore, 'users', auth.currentUser.uid), {
            emailVerified: true,
            verifiedAt: serverTimestamp(),
          });
        } catch (e) {
          console.warn('[VerifyEmail] Sync emailVerified failed (non-fatal):', e);
        }
        navigate(hasAccess ? '/dashboard' : '/paywall', { replace: true });
      } else {
        setError('Aún no detectamos la verificación. Revisa tu email e intenta de nuevo.');
      }
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="mobile-root">
      <section
        className="screen"
        style={{
          paddingTop: 60,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          paddingBottom: 40,
        }}
      >
        <AuthLogo />

        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 999,
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 12,
            marginBottom: 24,
          }}
        >
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </div>

        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            margin: 0,
            marginBottom: 8,
          }}
        >
          Revisa tu correo
        </h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, marginBottom: 22, maxWidth: 320, lineHeight: 1.5 }}>
          Te enviamos un email a <strong style={{ color: 'var(--ink)' }}>{user?.email}</strong> para verificar tu
          cuenta. Toca el enlace que te llegó.
        </p>

        <ErrorBanner msg={error} />

        {sent && (
          <div
            style={{
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.3)',
              color: '#22C55E',
              padding: '10px 14px',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            Email reenviado correctamente
          </div>
        )}

        <button className="btn-primary accent" style={{ width: '100%', maxWidth: 320 }} onClick={handleResend}>
          Reenviar email
        </button>

        {/* ISSUE 9: confirma verificación + redirige al destino correcto */}
        <button
          onClick={handleAlreadyVerified}
          disabled={refreshing}
          style={{
            width: '100%',
            maxWidth: 320,
            marginTop: 10,
            padding: '12px 20px',
            background: 'transparent',
            border: '1px solid var(--line-strong)',
            color: 'var(--ink)',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: refreshing ? 'default' : 'pointer',
            opacity: refreshing ? 0.7 : 1,
            fontFamily: 'inherit',
          }}
        >
          {refreshing ? 'Verificando…' : 'Ya verifiqué — continuar'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: 'var(--muted)' }}>
          ¿Email incorrecto?{' '}
          <a
            onClick={async () => {
              try { await signOut(); } catch (e) { /* best-effort */ }
              navigate('/signup');
            }}
            style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
          >
            Cambiar
          </a>
        </div>
      </section>
    </div>
  );
}
